import type { FlightingShape, FlightConfig, EffectiveFlightsResult } from "./flightPlacementTypes";

/**
 * Derives the list of "effective" flights used for placement.
 *
 * LOGIC:
 * - If the event has explicit flights (flighting.flights), we use those.
 * - If there are no flights but there are divisions, we treat each division
 *   as a single flight (divisionOnly: true). This avoids special-casing
 *   "division-only" mode elsewhere.
 *
 * Returns both the effective flight list and a flag useDivisionsAsFlights
 * so callers know whether we're in "one flight per division" mode (used
 * when matching registrations in manual placement).
 */
export function getEffectiveFlights(flighting: FlightingShape | undefined): EffectiveFlightsResult {
    const divisions = flighting?.divisions ?? [];
    const flights = (flighting?.flights ?? []) as FlightConfig[];

    // When no flights exist, treat each division as a single flight so that
    // placement logic can always iterate over "flights" uniformly.
    const effectiveFlights: FlightConfig[] =
        flights.length > 0
            ? flights
            : divisions.map((division: { divisionName?: string }, i: number) => ({
                  divisionIndex: i,
                  flightName: division.divisionName ?? `Division ${i + 1}`,
                  index: i,
                  players: [] as unknown[],
                  divisionOnly: true,
              }));

    const useDivisionsAsFlights = flights.length === 0 && divisions.length > 0;

    return { effectiveFlights, useDivisionsAsFlights };
}
